import { z } from "zod";

import {
  dataCompletenessStatusSchema,
  driverTagSchema,
  driverTypeSchema,
  outcomeInsightTagSchema,
  outcomeStatusAmountSchema,
  projectLifecyclePhaseSchema,
  recoverabilityStatusSchema,
} from "../schema";

export const SOURCE_EVIDENCE_CATEGORIES = [
  "project_metadata_and_contract",
  "schedule_and_time_tracking",
  "change_orders_and_claims",
  "financial_outcome",
  "decision_log_and_notices",
  "quality_and_self_control",
  "technical_scope_and_revisions",
  "procurement_and_execution_coordination",
  "material_consumption",
] as const;

export const sourceEvidenceCategorySchema = z.enum(SOURCE_EVIDENCE_CATEGORIES);
export const sourceConfidenceSchema = z.enum(["high", "medium", "low"]);

const sourceDocumentIdSchema = z.string().min(1);
const sourceDriverIdSchema = z.string().min(1);

export const sourceDocumentSchema = z
  .object({
    id: sourceDocumentIdSchema,
    projectFileId: z.string().min(1).nullable(),
    title: z.string().min(1),
    filePath: z.string().min(1).nullable(),
    docType: z.string().min(1).nullable(),
    category: sourceEvidenceCategorySchema,
    summarySnippet: z.string().min(1).nullable(),
    confidence: sourceConfidenceSchema.nullable(),
  })
  .strict();

export const sourceCoverageCategorySchema = z
  .object({
    key: sourceEvidenceCategorySchema,
    status: dataCompletenessStatusSchema,
    supportingDocumentIds: z.array(sourceDocumentIdSchema),
    notes: z.array(z.string().min(1)),
  })
  .strict();

const sourceImpactSchema = z
  .object({
    amountSek: z.number().finite().nullable(),
    hours: z.number().finite().nullable(),
    days: z.number().finite().nullable(),
  })
  .strict();

export const sourceDriverSchema = z
  .object({
    id: sourceDriverIdSchema,
    rank: z.number().int().positive(),
    title: z.string().min(1),
    narrative: z.string().min(1),
    type: driverTypeSchema,
    impact: sourceImpactSchema,
    originPhase: projectLifecyclePhaseSchema,
    discoveryPhase: projectLifecyclePhaseSchema,
    recoverability: z
      .object({
        status: recoverabilityStatusSchema,
        blockers: z.array(z.string().min(1)),
        requiredForRecovery: z.string().min(1).nullable(),
      })
      .strict()
      .nullable(),
    causeStatements: z.array(z.string().min(1)),
    tags: z.array(driverTagSchema),
    documentIds: z.array(sourceDocumentIdSchema).min(1),
  })
  .strict();

const sourceOutcomeTopDriverSchema = z
  .object({
    driverId: sourceDriverIdSchema,
    tags: z.array(outcomeInsightTagSchema),
  })
  .strict();

const sourceProjectFactsSchema = z
  .object({
    name: z.string().min(1),
    summaryShort: z.string().min(1),
    narrative: z.string().min(1),
    categoryLabel: z.string().min(1),
    locationLabel: z.string().min(1),
    year: z.number().int().nullable(),
    disciplineLabel: z.string().min(1).nullable(),
    clientLabel: z.string().min(1).nullable(),
  })
  .strict();

const sourceContractFactsSchema = z
  .object({
    contractForm: z.string().min(1).nullable(),
    contractValueSek: z.number().finite().nullable(),
  })
  .strict();

const sourceScheduleFactsSchema = z
  .object({
    plannedDurationDays: z.number().finite().nullable(),
    actualDurationDays: z.number().finite().nullable(),
    grantedExtensionDays: z.number().finite().nullable(),
    extraMobilizations: z.number().int().nonnegative().nullable(),
    delayPenaltyText: z.string().min(1).nullable(),
  })
  .strict();

const sourceCommercialFactsSchema = z
  .object({
    ataTotalSek: z.number().finite().nullable(),
    ataShareOfContractPct: z.number().finite().nullable(),
    statusBreakdown: z.array(outcomeStatusAmountSchema),
  })
  .strict();

const sourceOptionalFactsSchema = z
  .object({
    summary: z.string().min(1).nullable(),
  })
  .strict();

const sourceOutcomeProjectionSchema = z
  .object({
    money: z
      .object({
        ataTotalSek: z.number().finite().nullable(),
        ataShareOfContractPct: z.number().finite().nullable(),
        statusBreakdown: z.array(outcomeStatusAmountSchema),
        topDrivers: z.array(sourceOutcomeTopDriverSchema),
      })
      .strict(),
    time: z
      .object({
        unplannedDaysTotal: z.number().finite().nullable(),
        extraMobilizations: z.number().int().nonnegative().nullable(),
        grantedExtensionDays: z.number().finite().nullable(),
        delayPenaltyText: z.string().min(1).nullable(),
        topDrivers: z.array(sourceOutcomeTopDriverSchema),
      })
      .strict(),
  })
  .strict();

export const sourceEarlyWarningSchema = z
  .object({
    id: z.string().min(1),
    linkedDriverIds: z.array(sourceDriverIdSchema).min(1),
    title: z.string().min(1),
    signalText: z.string().min(1),
    earliestDetectablePhase: projectLifecyclePhaseSchema,
    discoveredInPhase: projectLifecyclePhaseSchema,
    possibleSavings: sourceImpactSchema,
    documentIds: z.array(sourceDocumentIdSchema).min(1),
  })
  .strict();

export const sourcePlaybookGroupSchema = z
  .object({
    phase: projectLifecyclePhaseSchema,
    items: z
      .array(
        z
          .object({
            id: z.string().min(1),
            actionText: z.string().min(1),
            ownerRole: z.string().min(1),
            linkedDriverIds: z.array(sourceDriverIdSchema).min(1),
            documentIds: z.array(sourceDocumentIdSchema).min(1),
          })
          .strict(),
      )
      .min(1),
  })
  .strict();

export const knowledgeBaseSourcePayloadSchema = z
  .object({
    schemaVersion: z.literal(1),
    projectId: z.string().uuid(),
    organizationId: z.string().uuid(),
    mode: z.literal("historical"),
    locale: z.literal("sv-SE"),
    currency: z.literal("SEK"),
    generatedAt: z.string().min(1),
    sourceVersion: z.string().min(1).nullable(),
    evidenceInventory: z
      .object({
        documents: z.array(sourceDocumentSchema),
      })
      .strict(),
    coverage: z
      .object({
        categories: z.array(sourceCoverageCategorySchema).min(1),
      })
      .strict(),
    facts: z
      .object({
        project: sourceProjectFactsSchema,
        contract: sourceContractFactsSchema,
        schedule: sourceScheduleFactsSchema,
        commercial: sourceCommercialFactsSchema,
        quality: sourceOptionalFactsSchema,
        technical: sourceOptionalFactsSchema,
        communications: sourceOptionalFactsSchema,
      })
      .strict(),
    driverGraph: z
      .object({
        drivers: z.array(sourceDriverSchema).min(1),
      })
      .strict(),
    projections: z
      .object({
        outcome: sourceOutcomeProjectionSchema,
        earlyWarnings: z.array(sourceEarlyWarningSchema).min(1),
        playbook: z.array(sourcePlaybookGroupSchema).min(1),
      })
      .strict(),
  })
  .strict()
  .superRefine((payload, ctx) => {
    const uniqueDocumentIds = new Set(
      payload.evidenceInventory.documents.map((document) => document.id),
    );
    if (uniqueDocumentIds.size !== payload.evidenceInventory.documents.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Document IDs must be unique.",
        path: ["evidenceInventory", "documents"],
      });
    }

    const uniqueDriverIds = new Set(payload.driverGraph.drivers.map((driver) => driver.id));
    if (uniqueDriverIds.size !== payload.driverGraph.drivers.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Driver IDs must be unique.",
        path: ["driverGraph", "drivers"],
      });
    }

    const addMissingDocumentIssue = (documentId: string, path: (string | number)[]) =>
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Missing document reference: ${documentId}`,
        path,
      });

    const addMissingDriverIssue = (driverId: string, path: (string | number)[]) =>
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Missing driver reference: ${driverId}`,
        path,
      });

    payload.coverage.categories.forEach((category, index) => {
      category.supportingDocumentIds.forEach((documentId) => {
        if (!uniqueDocumentIds.has(documentId)) {
          addMissingDocumentIssue(documentId, [
            "coverage",
            "categories",
            index,
            "supportingDocumentIds",
          ]);
        }
      });
    });

    payload.driverGraph.drivers.forEach((driver, index) => {
      driver.documentIds.forEach((documentId) => {
        if (!uniqueDocumentIds.has(documentId)) {
          addMissingDocumentIssue(documentId, ["driverGraph", "drivers", index, "documentIds"]);
        }
      });
    });

    payload.projections.outcome.money.topDrivers.forEach((ref, index) => {
      if (!uniqueDriverIds.has(ref.driverId)) {
        addMissingDriverIssue(ref.driverId, [
          "projections",
          "outcome",
          "money",
          "topDrivers",
          index,
          "driverId",
        ]);
      }
    });

    payload.projections.outcome.time.topDrivers.forEach((ref, index) => {
      if (!uniqueDriverIds.has(ref.driverId)) {
        addMissingDriverIssue(ref.driverId, [
          "projections",
          "outcome",
          "time",
          "topDrivers",
          index,
          "driverId",
        ]);
      }
    });

    payload.projections.earlyWarnings.forEach((warning, index) => {
      warning.linkedDriverIds.forEach((driverId) => {
        if (!uniqueDriverIds.has(driverId)) {
          addMissingDriverIssue(driverId, [
            "projections",
            "earlyWarnings",
            index,
            "linkedDriverIds",
          ]);
        }
      });

      warning.documentIds.forEach((documentId) => {
        if (!uniqueDocumentIds.has(documentId)) {
          addMissingDocumentIssue(documentId, [
            "projections",
            "earlyWarnings",
            index,
            "documentIds",
          ]);
        }
      });
    });

    payload.projections.playbook.forEach((group, groupIndex) => {
      group.items.forEach((item, itemIndex) => {
        item.linkedDriverIds.forEach((driverId) => {
          if (!uniqueDriverIds.has(driverId)) {
            addMissingDriverIssue(driverId, [
              "projections",
              "playbook",
              groupIndex,
              "items",
              itemIndex,
              "linkedDriverIds",
            ]);
          }
        });

        item.documentIds.forEach((documentId) => {
          if (!uniqueDocumentIds.has(documentId)) {
            addMissingDocumentIssue(documentId, [
              "projections",
              "playbook",
              groupIndex,
              "items",
              itemIndex,
              "documentIds",
            ]);
          }
        });
      });
    });
  });

export type SourceEvidenceCategory = z.output<typeof sourceEvidenceCategorySchema>;
export type SourceDocument = z.output<typeof sourceDocumentSchema>;
export type KnowledgeBaseSourcePayload = z.output<typeof knowledgeBaseSourcePayloadSchema>;
